/**
 * AIM Selective Menu hooks.
 *
 * Backs the WinForms `AIM_Utils.GenerateSMChanges` /
 * `SaveSMChanges` flow — the Selective Menu leg of the diet-order
 * AIM cascade. Runs AFTER the meal-pattern / adjustment AIM leg
 * completes (see `ResidentDietOrderManager.vb:216`).
 *
 * Facility gate: `AIM_DietOrder_UpdateSelectiveMenus(fac)`.
 * Discharge gate: `tblPatient.Discharged = true` short-circuits.
 * The server enforces BOTH — the hook still fires on every diet
 * change and the response's `skipReason` tells the caller why
 * it returned empty (if applicable).
 *
 * Save-side (`SaveSMChanges`) hasn't been wired yet — that lands
 * with the review dialog UI.
 */
import { useMutation } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import type { AimDietFlucsInput } from './aimDietApi';

/** Matches the C# `AimSMSkipReason` enum on the server. */
export type AimSMSkipReason =
  | 'none'
  | 'facilityGateDisabled'
  | 'patientDischarged'
  | 'noDietChangeImpact';

const SKIP_REASON_FROM_ENUM: Record<number, AimSMSkipReason> = {
  0: 'none',
  1: 'facilityGateDisabled',
  2: 'patientDischarged',
  3: 'noDietChangeImpact',
};

/**
 * Row-list dictionary — one entry per row in the underlying
 * DataTable, keyed by column name. The server flattens each of
 * the engine's 4 tables to this shape so the UI can pick the
 * columns it wants to render without a second round-trip.
 */
export type AimSMRowDict = Record<string, unknown>;

export interface AimSMRunResponse {
  hasProposals: boolean;
  skipReason: AimSMSkipReason;
  /** Unique (Day, Mealtime, MenuDate, FKey_Menu_Header) combos
   *  the engine would replace. */
  dayMealtimes: AimSMRowDict[];
  /** Row-level proposal grid — the UI toggles a `Selected` flag
   *  on each entry before posting Resolve. */
  combined: AimSMRowDict[];
  /** Currently-entered SM rows (source for the "before" column). */
  selectiveMenuOld: AimSMRowDict[];
  /** Proposed replacement SM rows. */
  selectiveMenuNew: AimSMRowDict[];
  /** What the printout (report 102, AIM Selective Menu Changes) says
   *  over the change list: the resident's unit and room, the diet
   *  order change, when and by whom AIM ran. Absent from servers
   *  that predate it. */
  reportContext?: AimSMReportContext | null;
}

/** Report 102's context of one AIM run (server `AimSMReportContext`).
 *  Dates are the facility's wall clock, `yyyy-MM-ddTHH:mm:ss`, no zone. */
export interface AimSMReportContext {
  residentUnit?: string;
  residentRoom?: string;
  dietOrderOld?: string;
  dietOrderNew?: string;
  dietOrderEffectiveDate?: string | null;
  aimRunDateTime?: string | null;
  aimRunBy?: string;
}

/**
 * WinForms `AIM_ModType` enum (EnumerationsConstantsCollections.
 * vb:313). Kept in ordinal alignment so the server can cast
 * directly.
 */
export const AIM_MOD_TYPE = {
  DietOrder: 0,
  MealPatternAdd: 1,
  MealPatternDeleteSilent: 2,
  MealPatternDelete: 3,
  Adjustment: 4,
} as const;
export type AimModType = typeof AIM_MOD_TYPE[keyof typeof AIM_MOD_TYPE];

/**
 * One entry from the session-scoped MP/Adj change accumulator
 * that the MP tab flushes on navigate-away. Mirrors WinForms
 * `AIM_SMChange` (AIM.vb:5-47) — full row-snapshot parity.
 *   • `row`      : full changed-row dict (null for DietOrder)
 *   • `children` : MP substitution children (null otherwise)
 *   • `mealtimes`: Adjustment mealtime list (null otherwise)
 */
export interface AimSMChangeInput {
  modType: AimModType;
  row?: Record<string, unknown> | null;
  children?: Array<Record<string, unknown>> | null;
  mealtimes?: number[] | null;
}

export interface AimSMRunInput {
  facilityId: number;
  patientId: number;
  rowDietFlucsOriginal: AimDietFlucsInput;
  rowDietFlucsModified: AimDietFlucsInput;
  /** Optional. When omitted / empty the server seeds a single
   *  DietOrder change (the existing diet-tuple chain). When
   *  present, mirrors WinForms `_lstAIM_SMChange`. */
  changes?: AimSMChangeInput[];
}

interface RawAimSMRunResponse {
  hasProposals: boolean;
  /** Server ships this as an int (the enum ordinal). */
  skipReason: number;
  dayMealtimes: AimSMRowDict[];
  combined: AimSMRowDict[];
  selectiveMenuOld: AimSMRowDict[];
  selectiveMenuNew: AimSMRowDict[];
  reportContext?: RawAimSMReportContext | null;
}

/** Server casing: the API's camelCase turns `AIMRunDateTime` into `aIMRunDateTime`. */
interface RawAimSMReportContext {
  residentUnit?: string;
  residentRoom?: string;
  dietOrderOld?: string;
  dietOrderNew?: string;
  dietOrderEffectiveDate?: string | null;
  aIMRunDateTime?: string | null;
  aimRunDateTime?: string | null;
  aIMRunBy?: string;
  aimRunBy?: string;
}

function toReportContext(raw: RawAimSMReportContext | null | undefined): AimSMReportContext | null {
  if (!raw) return null;
  return {
    residentUnit: raw.residentUnit ?? '',
    residentRoom: raw.residentRoom ?? '',
    dietOrderOld: raw.dietOrderOld ?? '',
    dietOrderNew: raw.dietOrderNew ?? '',
    dietOrderEffectiveDate: raw.dietOrderEffectiveDate ?? null,
    aimRunDateTime: raw.aIMRunDateTime ?? raw.aimRunDateTime ?? null,
    aimRunBy: raw.aIMRunBy ?? raw.aimRunBy ?? '',
  };
}

export function useAimSelectiveMenuRun() {
  return useMutation({
    mutationFn: async (input: AimSMRunInput): Promise<AimSMRunResponse> => {
      const { data } = await apiClient.post<RawAimSMRunResponse>(
        API.aimSelectiveMenuRun(input.facilityId, input.patientId),
        {
          FKey_Patient: input.patientId,
          RowDietFlucsOriginal: input.rowDietFlucsOriginal,
          RowDietFlucsModified: input.rowDietFlucsModified,
          Changes: input.changes && input.changes.length > 0
            ? input.changes.map((c) => ({
                ModType: c.modType,
                Row: c.row ?? null,
                Children: c.children ?? null,
                Mealtimes: c.mealtimes ?? null,
              }))
            : null,
        },
      );
      return {
        hasProposals: !!data?.hasProposals,
        skipReason: SKIP_REASON_FROM_ENUM[data?.skipReason ?? 0] ?? 'none',
        dayMealtimes: data?.dayMealtimes ?? [],
        combined: data?.combined ?? [],
        selectiveMenuOld: data?.selectiveMenuOld ?? [],
        selectiveMenuNew: data?.selectiveMenuNew ?? [],
        reportContext: toReportContext(data?.reportContext),
      };
    },
  });
}

// ─── Resolve (Save) ────────────────────────────────────────
//
// User clicked Save. Client sends back the 4 tables it got
// from Run with the user's `Selected` toggles applied on
// Combined. Server rebuilds a DataSet and calls
// `AIM_Utils.SaveSMChanges(...)`.

export interface AimSMResolveInput {
  facilityId: number;
  patientId: number;
  dayMealtimes: AimSMRowDict[];
  combined: AimSMRowDict[];
  selectiveMenuOld: AimSMRowDict[];
  selectiveMenuNew: AimSMRowDict[];
}

export function useAimSelectiveMenuResolve() {
  return useMutation({
    mutationFn: async (input: AimSMResolveInput): Promise<void> => {
      await apiClient.post(
        API.aimSelectiveMenuResolve(input.facilityId, input.patientId),
        {
          FKey_Patient: input.patientId,
          DayMealtimes: input.dayMealtimes,
          Combined: input.combined,
          SelectiveMenuOld: input.selectiveMenuOld,
          SelectiveMenuNew: input.selectiveMenuNew,
        },
      );
    },
  });
}

// ─── Revert-Old (Cancel-with-restore) ───────────────────────
//
// `GenerateSMChanges` DELETED the patient's SM rows for every
// affected (menu-header, date, mealtime) before returning. If
// the user cancels without saving, we MUST re-insert
// `selectiveMenuOld` or those days lose their entries silently.
// See the 2-tier Cancel prompt in the dialog for when this
// fires.

export interface AimSMRevertOldInput {
  facilityId: number;
  patientId: number;
  selectiveMenuOld: AimSMRowDict[];
}

export function useAimSelectiveMenuRevertOld() {
  return useMutation({
    mutationFn: async (input: AimSMRevertOldInput): Promise<void> => {
      await apiClient.post(
        API.aimSelectiveMenuRevertOld(input.facilityId, input.patientId),
        {
          FKey_Patient: input.patientId,
          SelectiveMenuOld: input.selectiveMenuOld,
        },
      );
    },
  });
}

// ─── Historical (Dashboard viewer) ─────────────────────────
//
// Fetches an already-applied auto-processed SM snapshot for the
// dashboard's Auto Generated Selective Menus card double-click
// viewer. Same 4-table response as Run so the AimSelectiveMenuDialog
// reuses. `dayMealtimes[i].ShowLock` tells the client whether that
// specific day/mealtime is stale (past date, or another human edit
// happened after auto-processing).

export interface AimSelectiveMenuHistoricalInput {
  facilityId: number;
  patientId: number;
  guid: string;
}

export function useAimSelectiveMenuHistorical() {
  return useMutation({
    mutationFn: async (input: AimSelectiveMenuHistoricalInput): Promise<AimSMRunResponse> => {
      const { data } = await apiClient.get<RawAimSMRunResponse>(
        API.aimSelectiveMenuHistorical(input.facilityId, input.patientId, input.guid),
      );
      return {
        hasProposals: !!data?.hasProposals,
        skipReason: SKIP_REASON_FROM_ENUM[data?.skipReason ?? 0] ?? 'none',
        dayMealtimes: data?.dayMealtimes ?? [],
        combined: data?.combined ?? [],
        selectiveMenuOld: data?.selectiveMenuOld ?? [],
        selectiveMenuNew: data?.selectiveMenuNew ?? [],
        reportContext: toReportContext(data?.reportContext),
      };
    },
  });
}

// ─── Resolve Dashboard (Save from historical viewer) ─────
//
// Dashboard save-back path. Same request shape as the AIM Resolve
// hook, but posts to a DIFFERENT endpoint that routes through
// `SelectiveMenuChanges_Historical.SaveHistorical` on the server
// (ports WinForms `SaveDataDashboard`). Only rewrites UNLOCKED
// mealtimes; the WinForms UI-level guard is enforced server-side
// via `Select("ShowLock = FALSE")` so a stale client can't
// squeeze in a write to a mealtime that has since been edited.
export function useAimSelectiveMenuResolveDashboard() {
  return useMutation({
    mutationFn: async (input: AimSMResolveInput): Promise<void> => {
      await apiClient.post(
        API.aimSelectiveMenuResolveDashboard(input.facilityId, input.patientId),
        {
          FKey_Patient: input.patientId,
          DayMealtimes: input.dayMealtimes,
          Combined: input.combined,
          SelectiveMenuOld: input.selectiveMenuOld,
          SelectiveMenuNew: input.selectiveMenuNew,
        },
      );
    },
  });
}
